import { Divider, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IUser, { IUserAccessProfileEnterprise } from "../../../../interfaces/IUser";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusData, RegistrationStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import UserService from '../../../../services/UserService';
import IAccessProfile from "../../../../interfaces/IAccessProfile";
import AccessProfileService from "../../../../services/AccessProfileService";
import IEnterprise from "../../../../interfaces/IEnterprise";
import SelectField from "../../../../components/SelectField";
import Tooltip from "../../../../components/Tooltip";
import useState from "../../../../hooks/useState";


interface IUserAddEditDialogProps extends IAddEditDialogProps<IUser> {
    enterprises: IEnterprise[];
}

interface IStateValues {
    accessProfiles: IAccessProfile[];

    fullName: string;
    login: string;
    email: string;
    password: string;
    partnerId: string;
    active: boolean;
    userAccessProfileEnterprise: Partial<IUserAccessProfileEnterprise>[];
    allowAllEnterprisesViewedInAnalyticalDashboard: boolean;
    allowAllEnterprisesViewedInManagementDashboard: boolean;
    allowAllEnterprisesViewedInFinancialDashboard: boolean;
    allowAllEnterprisesViewedInVacationCenterDashboard: boolean;
}


const RegistrationStatusInactive = RegistrationStatusData[RegistrationStatusEnum.Inactive];

const AddEditDialog: React.FunctionComponent<IUserAddEditDialogProps> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {},
    enterprises
}) => {
    const { translate } = useLanguageContext();

    const state = useState<IStateValues>({
        accessProfiles: [],

        fullName: '',
        login: '',
        email: '',
        password: '',
        partnerId: '',
        active: true,
        userAccessProfileEnterprise: [],
        allowAllEnterprisesViewedInAnalyticalDashboard: false,
        allowAllEnterprisesViewedInManagementDashboard: false,
        allowAllEnterprisesViewedInFinancialDashboard: false,
        allowAllEnterprisesViewedInVacationCenterDashboard: false,
    });


    useEffect(() => {
        if (open) {
            state.reset();
            
            if (data) {
                state.update('fullName', data.fullName);
                state.update('login', data.login);
                state.update('email', data.email);
                state.update('partnerId', data.partnerId);
                state.update('active', Number(data.registrationStatusId) === RegistrationStatusEnum.Active);
                state.update('userAccessProfileEnterprise', data.enterprises);
                state.update('allowAllEnterprisesViewedInAnalyticalDashboard', data.allowAllEnterprisesViewedInAnalyticalDashboard);
                state.update('allowAllEnterprisesViewedInManagementDashboard', data.allowAllEnterprisesViewedInManagementDashboard);
                state.update('allowAllEnterprisesViewedInFinancialDashboard', data.allowAllEnterprisesViewedInFinancialDashboard);
                state.update('allowAllEnterprisesViewedInVacationCenterDashboard', data.allowAllEnterprisesViewedInVacationCenterDashboard);
            }
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        if (state.values.accessProfiles.length === 0) {
            let formData = FormHelper.Create(undefined);
            formData = FormHelper.Add(formData, 'registrationStatus', RegistrationStatusEnum.Active);
            
            AccessProfileService.List(formData)
                .then(resp => {
                    const accessProfiles = resp.data.accessProfiles as IAccessProfile[];
                    state.update('accessProfiles', accessProfiles);
                });
        }
        // eslint-disable-next-line
    }, [state.values.accessProfiles.length]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id && data.id !== 0) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        UserService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const handleChangeEnterpriseAccessProfile = useCallback((enterprise: IEnterprise, accessProfileId: number, userPartnerAccessToken: string) => {
        const userAccessProfileEnterpriseAux = state.values.userAccessProfileEnterprise;

        const index = userAccessProfileEnterpriseAux.findIndex(e => e.id === enterprise.id);
        if (index > -1) {
            userAccessProfileEnterpriseAux.splice(index, 1);
        }

        userAccessProfileEnterpriseAux.push({
            id: enterprise.id,
            accessProfileId: accessProfileId,
            userPartnerAccessToken: userPartnerAccessToken
        });

        state.update('userAccessProfileEnterprise', userAccessProfileEnterpriseAux);
        // eslint-disable-next-line
    }, [enterprises, state.values]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data && data.id !== 0 ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('fullName')} 
                        name='fullName' 
                        value={state.values.fullName} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('partnerId')} 
                        name='partnerId' 
                        value={state.values.partnerId} 
                        required={!data?.administrator}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 20))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('user')} 
                        name='login' 
                        value={state.values.login} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value.toUpperCase(), 20))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        type='password'
                        label={translate('password')} 
                        name='password' 
                        value={state.values.password} 
                        required={!data?.id}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value.trim(), 20))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        type='email'
                        label={translate('email')} 
                        name='email' 
                        value={state.values.email} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('active')}
                        name='registrationStatusId'
                        value={state.values.active ? RegistrationStatusEnum.Active : RegistrationStatusEnum.Inactive}
                        checked={state.values.active}
                        onChange={e => state.update('active', e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12} marginBottom={2}>
                    <DialogSectionTitle title={translate('enterprises')} />
                </Grid>

                <Grid item xs={12}>
                    <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                        {
                            enterprises.map(enterprise => {
                                const userAccessProfileEnterprise = state.values.userAccessProfileEnterprise;

                                const accessProfileId = userAccessProfileEnterprise.find(e => e.id === enterprise.id)?.accessProfileId ?? 0;
                                const userPartnerAccessToken = userAccessProfileEnterprise.find(e => e.id === enterprise.id)?.userPartnerAccessToken ?? '';

                                return <Grid item md={4} sm={6} xs={12} key={enterprise.id} alignItems='stretch'>
                                    <Grid item className='enterprisebox-grid'>
                                        <Grid item xs={12} className='enterprisebox-header'>
                                            <Typography className='overflow-hidden textoverflow-ellipsis whitespace-nowrap'>
                                                {enterprise.name}
                                            </Typography>

                                            {
                                                Number(enterprise.registrationStatusId) === RegistrationStatusEnum.Inactive && (
                                                    <Tooltip title={translate('inactiveEnterprise')}>
                                                        <IconButton size="small">
                                                            <RegistrationStatusInactive.Icon className='fontsize-14' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            }
                                        </Grid>

                                        <Grid item xs={12} marginY={1} marginBottom={2}>
                                            <Divider />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <SelectField 
                                                label={translate('accessProfile')}
                                                name={`userAccessProfileEnterprises[${enterprise.id}][accessProfileId]`} 
                                                value={accessProfileId}
                                                onChange={e => handleChangeEnterpriseAccessProfile(enterprise, e.target.value, userPartnerAccessToken)}
                                                margin='none'
                                                data={[
                                                    { value: 0, text: translate('blockedAccess') },
                                                    ...state.values.accessProfiles.map(a => ({ value: a.id, text: a.description }))
                                                ]}
                                            />
                                        </Grid>

                                        <Grid item xs={12} paddingY={1}>
                                            <TextField 
                                                label={translate('accessToken')}
                                                name={`userAccessProfileEnterprises[${enterprise.id}][userPartnerAccessToken]`} 
                                                value={userPartnerAccessToken} 
                                                margin="none"
                                                onChange={e => handleChangeEnterpriseAccessProfile(enterprise, accessProfileId, e.target.value)}
                                                required={false}
                                                disabled={accessProfileId === 0}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>;
                            })
                        }
                    </Grid>
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12} marginBottom={2}>
                    <DialogSectionTitle title={translate('parameters')} />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('allowAllEnterprisesViewedInAnalyticalDashboard')}
                        name='allowAllEnterprisesViewedInAnalyticalDashboard'
                        value={state.values.allowAllEnterprisesViewedInAnalyticalDashboard}
                        checked={state.values.allowAllEnterprisesViewedInAnalyticalDashboard}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('allowAllEnterprisesViewedInManagementDashboard')}
                        name='allowAllEnterprisesViewedInManagementDashboard'
                        value={state.values.allowAllEnterprisesViewedInManagementDashboard}
                        checked={state.values.allowAllEnterprisesViewedInManagementDashboard}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('allowAllEnterprisesViewedInFinancialDashboard')}
                        name='allowAllEnterprisesViewedInFinancialDashboard'
                        value={state.values.allowAllEnterprisesViewedInFinancialDashboard}
                        checked={state.values.allowAllEnterprisesViewedInFinancialDashboard}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginLeft={1}>
                    <Switch
                        label={translate('allowAllEnterprisesViewedInVacationCenterDashboard')}
                        name='allowAllEnterprisesViewedInVacationCenterDashboard'
                        value={state.values.allowAllEnterprisesViewedInVacationCenterDashboard}
                        checked={state.values.allowAllEnterprisesViewedInVacationCenterDashboard}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;